import { displayDALocation } from '../lib/daDisplay';
import { summariseTrackedPipeline } from '../lib/trackedPipelineSummary';

const PERIOD_DAYS = 90;

function fmtMoney(v) {
  const value = Number(v || 0);
  if (!value) return '$0';
  if (value >= 1000000000) return `$${(value / 1000000000).toFixed(value >= 10000000000 ? 0 : 1)}B`;
  if (value >= 1000000) return `$${(value / 1000000).toFixed(value >= 100000000 ? 0 : 1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

function followUpLabel(iso) {
  if (!iso) return '';
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '';
  const days = Math.round((due.getTime() - Date.now()) / 86400000);
  if (days < -1) return `${Math.abs(days)} days overdue`;
  if (days === -1) return 'overdue since yesterday';
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `due in ${days} days`;
}

const STAGE_LABELS = [
  ['watching', 'Watching'],
  ['contacted', 'Contacted'],
  ['quoted', 'Quoted'],
  ['won', 'Won'],
];

export default function PipelineOutcomesCard({
  theme,
  userId = null,
  isPro = false,
  tracked = [],
  onOpenTracked,
  collapsed = false,
  onCollapsedChange = () => {},
}) {
  if (!userId || !isPro || !tracked.length) return null;
  const outcomes = summariseTrackedPipeline(tracked, Date.now(), PERIOD_DAYS);

  const stages = outcomes.stages || {};
  const dueFollowUps = Array.isArray(outcomes.due_follow_ups) ? outcomes.due_follow_ups : [];
  const winRate = Number.isFinite(Number(outcomes.win_rate)) && outcomes.win_rate !== null
    ? Number(outcomes.win_rate)
    : null;
  const quotedOpenValue = Number(outcomes.quoted_open_value || 0);
  const quotedOpenCount = Number(outcomes.quoted_open_count || 0);
  const wonValue = Number(outcomes.won_value || 0);
  const wonCount = Number(outcomes.won_count || 0);
  const isCollapsed = collapsed === true;
  const toggleCollapsed = () => onCollapsedChange(!isCollapsed);

  return (
    <div
      data-testid="pipeline-outcomes-card"
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 14,
        padding: 16,
        marginBottom: 12,
        boxShadow: '0 2px 12px rgba(0,0,0,0.35)',
      }}
    >
      <div
        onClick={toggleCollapsed}
        role="button"
        aria-expanded={!isCollapsed}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleCollapsed();
          }
        }}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: isCollapsed ? 0 : 10, cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6a80a0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)', transition: 'transform 0.2s', flexShrink: 0 }}>
            <polyline points="9 18 15 12 9 6"/>
          </svg>
          <span style={{ color: theme.accent, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
            Your Pipeline
          </span>
        </div>
        <span style={{ color: theme.textMuted, fontSize: 11 }}>All tracked projects</span>
      </div>

      {!isCollapsed && (
      <>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ color: '#6a80a0', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Quoted open</div>
          <div style={{ color: theme.text, fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
            {fmtMoney(quotedOpenValue)}
            <span style={{ color: theme.textMuted, fontSize: 12, fontWeight: 500 }}> · {quotedOpenCount} quote{quotedOpenCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div>
          <div style={{ color: '#6a80a0', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Won · last 90 days</div>
          <div style={{ color: '#5aba6a', fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
            {fmtMoney(wonValue)}
            <span style={{ color: theme.textMuted, fontSize: 12, fontWeight: 500 }}> · {wonCount} job{wonCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
        {winRate !== null && (
          <div>
            <div style={{ color: '#6a80a0', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Win rate · last 90 days</div>
            <div style={{ color: theme.text, fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{winRate}%</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STAGE_LABELS.map(([key, label]) => {
          const s = stages[key];
          if (!s || !Number(s.count)) return null;
          return (
            <span
              key={key}
              style={{
                border: `1px solid ${theme.border}`,
                borderRadius: 999,
                padding: '4px 10px',
                fontSize: 11,
                color: theme.textMuted,
                fontWeight: 600,
              }}
            >
              {label} {s.count}
              {Number(s.value) > 0 && <span style={{ color: theme.text }}> · {fmtMoney(s.value)}</span>}
            </span>
          );
        })}
      </div>

      <p style={{ color: theme.textMuted, fontSize: 12 }}>Watching/contacted: declared development budgets. Quoted/won: your recorded amounts only; missing amounts are excluded.</p>
      {onOpenTracked && (
        <button
          type="button"
          onClick={onOpenTracked}
          style={{ background: 'transparent', border: 'none', color: theme.text, cursor: 'pointer', font: 'inherit', padding: '8px 0', textDecoration: 'underline', textUnderlineOffset: 3 }}
        >
          View tracked projects →
        </button>
      )}

      {dueFollowUps.length > 0 && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${theme.border}`, paddingTop: 10 }}>
          <div style={{ color: '#6a80a0', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Follow-ups due
          </div>
          {dueFollowUps.map(f => (
            <div key={f.tracked_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '3px 0' }}>
              <span style={{ color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayDALocation(f, f.da_number || 'Street address not provided')}
              </span>
              <span style={{ color: f.overdue ? theme.accent : theme.textMuted, fontWeight: f.overdue ? 700 : 500, flexShrink: 0 }}>
                {followUpLabel(f.next_follow_up_at)}
              </span>
            </div>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}
